import { ApiService } from ".";
import IOrgsApiService from "./IOrgsApiService";
import { PagedModel } from "../models/PagedModel";
import OrgKeyModel from "../models/OrgKeyModel";
import KeyRotationPassphraseModel from "../models/KeyRotationPassphraseModel";
import KeyRotationStatusModel from "../models/KeyRotationStatusModel";
import ScheduleModel from "../models/ScheduleModel";
import ReminderModel from "../models/KeyRotationReminderModel";

export default class OrgsApiService extends ApiService implements IOrgsApiService {
    constructor(baseUrl: string, language?: string) {
        super(baseUrl, language);
    }

    public getPublicKeys(
        orgId: string,
        page: number = 1,
        perPage: number = 25,
        sortField: "timestamp",
        sortDirection: "ascending" | "descending" = "descending"
    ): Promise<PagedModel<OrgKeyModel>> {
        return this.get<PagedModel<OrgKeyModel>>(
            this.makeUrl(orgId, "/keys"),
            {
                page,
                perPage,
                sortField,
                sortDirection,
            },
            true
        );
    }

    public beginKeyRotationEstimate(orgId: string): Promise<KeyRotationStatusModel> {
        return this.post<KeyRotationStatusModel>(
            this.makeUrl(orgId, "/keys/rotation/estimate"),
            {}
        );
    }

    public removeKeyRotationEstimate(orgId: string): Promise<void> {
        return this.delete<void>(this.makeUrl(orgId, "/keys/rotation/estimate"));
    }

    public getKeyRotationPassphrase(orgId: string): Promise<KeyRotationPassphraseModel> {
        return this.get<KeyRotationPassphraseModel>(
            this.makeUrl(orgId, "/keys/passphrase"),
            {},
            true
        );
    }

    public validateKeyRotationPassphrase(orgId: string, passphrase: string): Promise<void> {
        return this.post<void>(this.makeUrl(orgId, "/keys/passphrase"), { passphrase });
    }

    public getKeyRotationReminder(orgId: string): Promise<ReminderModel> {
        return this.get<ReminderModel>(this.makeUrl(orgId, "/keys/reminder"), {}, true);
    }

    public setKeyRotationReminder(orgId: string, reminderDate: Date): Promise<ReminderModel> {
        return this.post<ReminderModel>(this.makeUrl(orgId, "/keys/reminder"), { reminderDate });
    }

    public beginKeyRotation(
        orgId: string,
        passphrase: string,
        schedule: ScheduleModel
    ): Promise<KeyRotationStatusModel> {
        return this.post<KeyRotationStatusModel>(this.makeUrl(orgId, "/keys/rotation"), {
            passphrase,
            schedule,
        });
    }

    public pauseKeyRotation(orgId: string): Promise<KeyRotationStatusModel> {
        return this.post<KeyRotationStatusModel>(this.makeUrl(orgId, "/keys/rotation/pause"), {});
    }

    public resumeKeyRotation(orgId: string): Promise<KeyRotationStatusModel> {
        return this.post<KeyRotationStatusModel>(this.makeUrl(orgId, "/keys/rotation/resume"), {});
    }

    public setKeyRotationSchedule(
        orgId: string,
        schedule: ScheduleModel
    ): Promise<KeyRotationStatusModel> {
        return this.post<KeyRotationStatusModel>(this.makeUrl(orgId, "/keys/rotation/schedule"), {
            schedule,
        });
    }

    public getKeyRotationStatus(orgId: string): Promise<KeyRotationStatusModel> {
        return this.get<KeyRotationStatusModel>(this.makeUrl(orgId, "/keys/rotation"), {}, true);
    }

    public removeKeyRotationStatus(orgId: string): Promise<void> {
        return this.delete<void>(this.makeUrl(orgId, "/keys/rotation"));
    }

    private makeUrl(orgId: string, endpoint?: string) {
        return `organizations/${orgId}${endpoint}`;
    }
}
